import {
  SvgBadgeDiscount,
  SvgChatQuestion,
  SvgEvent,
  SvgFileTextHeart,
  SvgNotice,
  SvgReceiptText,
  SvgReferral,
  SvgStarTag,
} from '@common/icon';
import { usePathname } from 'next/navigation';
import { useShallow } from 'zustand/react/shallow';

import { DONATION_ROUTES, ROUTES, SUB_DOMAIN } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useAuthStore } from '@/stores/AuthStore';
import { useDeviceInfoStore } from '@/stores/DeviceStore';
import { openResponsiveDialog } from '@/stores/DrawerStore';

import { JejuCertificateContent } from '../(private)/my/(benefit)/jeju/(certificate)/_components/JejuCertificateContent';

interface NavigationItem {
  label: string;
  href: string;
  icon: React.ReactElement;
  isLoginRequired: boolean;
  isNew?: boolean;
  onClick?: () => void;
}

interface NavigationGroup {
  label: string;
  items: NavigationItem[];
}

export const useNavigationGroupList = () => {
  const isLogin = useAuthStore((state) => state.isLogin) ?? false;
  const router = useCustomRouter();
  const pathname = usePathname();

  const [isToss, isKbPay] = useDeviceInfoStore(
    useShallow(
      (state) =>
        [
          state.subDomain === SUB_DOMAIN.TOSS,
          state.subDomain === SUB_DOMAIN.KB_PAY,
        ] as const,
    ),
  );

  const NAVIGATION_GROUP_LIST = {
    MY_DONATIONS: {
      label: '나의 기부',
      items: [
        {
          label: '기부내역',
          href: DONATION_ROUTES.MY_DONATIONS.pathname,
          icon: <SvgFileTextHeart className="size-20" originalFill />,
          isLoginRequired: true,
          isNew: false,
        },
      ],
    },

    MY_GIFTS: {
      label: '나의 답례품',
      items: [
        {
          label: '장바구니',
          href: DONATION_ROUTES.CART.pathname,
          icon: <SvgFileTextHeart className="size-20" originalFill />,
          isLoginRequired: true,
          isNew: false,
        },
        {
          label: '주문 · 배송 내역',
          href: ROUTES.MY_ORDERS.pathname,
          icon: <SvgReceiptText className="size-20" originalFill />,
          isLoginRequired: true,
          isNew: false,
        },
        {
          label: '배송지 관리',
          href: ROUTES.MY_DELIVERY_ADDRESS.pathname,
          icon: <SvgFileTextHeart className="size-20" originalFill />,
          isLoginRequired: true,
          isNew: false,
        },
      ],
    },

    MY_BENEFITS: (isTossOrKbPay: boolean) => ({
      label: '나의 혜택',
      items: [
        {
          label: '탐나는 제주패스',
          href: '#',
          onClick: () => {
            if (!isLogin) {
              return router.push(
                ROUTES.LOGIN.withSearchParams({
                  searchParams: {
                    redirect: pathname || '',
                  },
                }),
              );
            }

            openResponsiveDialog({
              component: JejuCertificateContent,
              drawerType: 'bottom',
            });
          },
          icon: <SvgBadgeDiscount className="size-20" originalFill />,
          isLoginRequired: true,
          isNew: false,
        },
        ...(isTossOrKbPay
          ? []
          : [
              {
                label: '친구 초대 현황',
                href: DONATION_ROUTES.MY_INVITE.pathname,
                icon: <SvgReferral className="size-20" originalFill />,
                isLoginRequired: true,
                isNew: true,
              },
            ]),
        {
          label: '웰포인트 상점',
          href: ROUTES.POINT_MARKET.pathname,
          icon: <SvgEvent className="size-20" originalFill />,
          isLoginRequired: true,
          isNew: true,
          onClick: () => {
            if (!isLogin) {
              return router.push(
                ROUTES.LOGIN.withSearchParams({
                  searchParams: {
                    redirect: pathname || '',
                  },
                }),
              );
            }

            router.push(ROUTES.POINT_MARKET.pathname);
          },
        },
      ],
    }),
    MY_ACTIVITIES: {
      label: '나의 활동',
      items: [
        {
          label: '문의 내역',
          href: DONATION_ROUTES.MY_INQUIRY_HISTORY.pathname,
          icon: <SvgChatQuestion className="size-20" originalFill />,
          isLoginRequired: true,
        },
        {
          label: '답례품 후기',
          href: ROUTES.MY_REVIEWS.pathname,
          icon: <SvgStarTag className="size-20" originalFill />,
          isLoginRequired: true,
          isNew: false,
        },
      ],
    },

    INFO_GUIDE: {
      label: '안내',
      items: [
        {
          label: '공지사항',
          href: DONATION_ROUTES.MY_ETC_NOTICE.pathname,
          icon: <SvgNotice className="size-20" originalFill />,
          isLoginRequired: false,
          isNew: false,
        },
        {
          label: '이벤트',
          href: DONATION_ROUTES.MY_ETC_EVENT.pathname,
          icon: <SvgEvent className="size-20" originalFill />,
          isLoginRequired: false,
          isNew: false,
        },
      ],
    },
  };

  const DEFAULT_NAVIGATION_GROUP_LIST: NavigationGroup[] = [
    NAVIGATION_GROUP_LIST.MY_DONATIONS,

    NAVIGATION_GROUP_LIST.MY_GIFTS,

    NAVIGATION_GROUP_LIST.MY_BENEFITS(isToss || isKbPay),
    NAVIGATION_GROUP_LIST.MY_ACTIVITIES,
    NAVIGATION_GROUP_LIST.INFO_GUIDE,
  ];

  return {
    navigationGroupList: DEFAULT_NAVIGATION_GROUP_LIST,
  };
};
